/**
 * Authorization Types
 *
 * Core type definitions for the authorization system
 */

/**
 * Actions that can be performed on resources
 */
export enum Action {
  Create = "create",
  Read = "read",
  Update = "update",
  Delete = "delete",
  Approve = "approve",
  Manage = "manage", // Special action that grants all permissions
  Execute = "execute", // Execute operations like processing payments, transfers
  Report = "report", // Generate reports and analytics
  ChangePlanningLevelRules = "change_planning_level_rules", // Inventory forecast settings rule order
  Tag = "tag", // Apply/remove policy & descriptive tags on a resource (ABAC)
}

/**
 * Subject types that can have permissions
 *
 * Organized by functional area for clarity.
 */
/**
 * Kinds of static (system-managed) user group. Shared so the backend model, the
 * RPC contract and the frontend agree on one closed set. `staff_admin` is the
 * per-organization Gently Staff Admin group.
 */
export enum UserGroupStaticKind {
  StaffAdmin = "staff_admin",
}

export enum Subject {
  // Special
  All = "all", // Reserved for Gently staff bypass only

  // Finance & Accounting
  APInvoice = "APInvoice",
  APInvoicePayment = "APInvoicePayment",
  // Dedicated capability: the AP Command Center dashboard (metric tiles +
  // exception queue). Gated independently of APInvoice read so orgs can
  // grant the operations dashboard without a feature flag.
  APCommandCenter = "APCommandCenter",
  // Temporary CFO throughput view (emails, drafts, auto-accept, Famous
  // purchase-match posts). Its own subject so AP invoice readers and
  // Command Center viewers do not see it. Not granted by any default group.
  APThroughputDashboard = "APThroughputDashboard",
  // AP exception rule sets (`ap_exception_rule_sets`): the exception
  // vocabulary + per-type ownership a workflow's match node binds. Its own
  // subject because who-owns-which-exception is a control decision, distinct
  // from editing invoices.
  APExceptionRuleSet = "APExceptionRuleSet",
  ARInvoice = "ARInvoice",
  // AR cash: cash receipts, remittances, their links and applications
  // (GEN-3091; replaced ARPayment).
  ARCash = "ARCash",
  GeneralLedger = "GeneralLedger",
  Account = "Account",
  Budget = "Budget",
  FixedAsset = "FixedAsset",

  // Sales & CRM
  SalesOrder = "SalesOrder",
  SalesOrderDraft = "SalesOrderDraft",
  SalesOrderDraftV3 = "SalesOrderDraftV3",
  // Dedicated capability: reassign a draft sales order to another organization
  // (gated independently of the SalesOrderDraftV3 read/update/approve grants).
  SalesOrderDraftReassign = "SalesOrderDraftReassign",
  Discount = "Discount",
  // Discounts v2 (OMS v3): discount groups + elements + draft-line stacks.
  // Gated separately from the legacy v1 Discount subject so orgs can roll the
  // v2 surface out independently.
  DiscountV2 = "DiscountV2",
  ProductMargins = "ProductMargins",
  ProductCatalog = "ProductCatalog",
  Customer = "Customer",
  Contact = "Contact",
  // Customer SKU → our-product mappings: the Famous EDI translation list
  // (`famous_edi_translation_list_items`) and its legacy `ar_customer_products`
  // representation. Gated separately from Customer so refreshing a stale
  // mapping doesn't require the ability to edit customer master data.
  EdiTranslationList = "EdiTranslationList",
  // Vendor EDI implementation guidelines (`edi_guidelines`) — the documents
  // stating what a trading partner will actually transmit. Its own subject
  // because a guideline is the spec other reads are interpreted against:
  // holding org-settings access should not carry the ability to change what a
  // vendor is recorded as having said.
  EdiGuideline = "EdiGuideline",
  ServiceTicket = "ServiceTicket",
  Payment = "Payment",

  // Purchasing
  PurchaseOrder = "PurchaseOrder",
  Supplier = "Supplier",
  // Supplier part-number → our-product mappings (ap_supplier_products rows
  // that carry supplierFreeform). Separate from Supplier so seeing or editing
  // supplier master data does not reveal this page. Not granted by any
  // default group.
  SupplierProductMatch = "SupplierProductMatch",
  ExternalPriceSheet = "ExternalPriceSheet",
  // Per-branch freight options for purchase orders (carrier, freight terms,
  // carrier account, weight and value limits). Read lists them and fills the
  // PO freight picker; Create adds a manual option; Update confirms, edits,
  // archives and refreshes the suggestions learned from past releases.
  FreightOption = "FreightOption",

  // Pricing
  Pricing = "Pricing",
  // Price list master data: synced ERP price lists and manually uploaded
  // exception-price-list overlays. Gated separately from Customer so managing
  // org-wide pricing requires its own grant.
  PriceList = "PriceList",

  // Inventory
  Product = "Product",
  Inventory = "Inventory",
  Warehouse = "Warehouse",
  // The supply network: location roles, lanes and sourcing rules. Separate from
  // Warehouse because publishing changes what the replenishment engine orders
  // and transfers for the whole organization.
  SupplyNetwork = "SupplyNetwork",
  // Code Schemes: how an organization's Short Codes are built and which one is
  // shown. Separate from Warehouse and Division because a scheme changes how
  // every code of a type reads. Read lists schemes, Create adds one, Update
  // chooses the display scheme.
  CodeScheme = "CodeScheme",

  // Rebates
  RebateConfig = "RebateConfig",
  RebateAgreement = "RebateAgreement",
  RebateDataUpload = "RebateDataUpload",

  // Ship & Debit
  SDTransaction = "SDTransaction",
  VDM = "VDM",
  SD849Response = "SD849Response",
  SDActivityLog = "SDActivityLog",
  SDAdmin = "SDAdmin",
  SDPosting = "SDPosting",
  SD844Resubmission = "SD844Resubmission",
  SDTransactionV2 = "SDTransactionV2",

  // Organization & Administration
  Company = "Company",
  User = "User",
  UserGroup = "UserGroup",
  // Viewing/editing which permissions a group holds (the permission matrix),
  // separate from renaming groups or managing their members (UserGroup).
  // Present in both the Staff Admin and the customer permission catalogs.
  UserGroupPermissions = "UserGroupPermissions",
  // Gently staff: viewing/editing the static "Staff Admin" group - its
  // permissions AND its members. Never offered in a customer catalog.
  StaffAdminPermission = "StaffAdminPermission",
  WorkflowJsonDiff = "WorkflowJsonDiff",
  // Gently staff: the staff-only ("gentlyOnly") sections of Manage
  // Organization. Update-only on purpose - even viewing them is staff-only,
  // because everyone needs to READ org config (that is OrganizationConfig).
  OrganizationSettings = "OrganizationSettings",
  OrganizationConfig = "OrganizationConfig",
  // Changing an organization's module configuration: enabling/disabling a
  // module and editing its settings. Update-only on purpose - every page
  // reads module config (accounting dialogs, procurement thresholds, workflow
  // renderers), so there is no separate Read grant to hand out.
  OrganizationModule = "OrganizationModule",
  Division = "Division",
  Integration = "Integration",
  MemoryBankNote = "MemoryBankNote",
  // Agent-harness memory: typed rules the harness applies deterministically
  // (`core_memory_rules`) and the proposals a human approves them from
  // (`core_memory_proposals`). Approve on MemoryProposal is what activates a
  // rule; the learning job itself never can.
  MemoryRule = "MemoryRule",
  MemoryProposal = "MemoryProposal",
  // The tool-call ledger behind Org Settings › Agent Harness › Tool Calls:
  // every chat-agent tool call by module, page, tool, user and outcome.
  // Without Read, a user only sees their own calls.
  HarnessToolCall = "HarnessToolCall",
  // Estimated AI model spend attributed to tasks and their agent runs.
  // Read-only. Not granted by any default group: AI cost was Gently-staff only,
  // and staff keep it through the isAdminOrCan bridge; a customer team sees it
  // only when granted. Reading a task's cost also needs Task read.
  AiCost = "AiCost",
  // Connect external AI clients through MCP. Does not grant tool permissions.
  McpServer = "McpServer",
  BackgroundAgent = "BackgroundAgent",
  Project = "Project",
  Workbook = "Workbook",
  // Data platform inputs a Workbook can read. `read` = may list and read the
  // rows of Data Source links, Data Mappings and Data Assets (Workbook Sources).
  DataSource = "DataSource",
  DataMapping = "DataMapping",
  DataAsset = "DataAsset",
  Task = "Task",
  // The task type registry (core_task_types): create/update/archive, AI
  // stage generation, feeds and JSON import (`create`) / export (`read`).
  // Listing types for pickers still accepts Task.Read. Replaces the old
  // Gently-staff (`isAdmin`) hard gate; staff keep access via the bypass.
  TaskType = "TaskType",
  // Org-configurable task-queue pages (core_task_pages): `read` = may see
  // task pages at all; per-page visibility is further narrowed by each page's
  // user-group audience (a join table, not an RBAC subject — subjects are
  // compile-time constants). Create/Update/Delete gate page administration.
  TaskPage = "TaskPage",
  // Org namespaces (`core_namespaces`): Linear-style groupings that number
  // tasks and hold task types, workflows, and agents. `read` = the /namespaces
  // admin inventory; listing for task pickers still accepts Task.Read.
  // Create/Update/Delete gate namespace administration (replaces the old
  // organization-admin hard gate).
  Namespace = "Namespace",
  // ERP Apps (`apps`): agent-built, json-render pages over tool calls.
  // `read` = open and use Apps; `create` = author and deploy versions;
  // `update` = activate versions and change the auto-update toggle. Gently
  // staff pass via `isAdminOrCan` while customers cannot author yet.
  App = "App",
  // In-app feedback (`core_feedback`). Any authenticated user may file
  // one, so there is no Create grant. `read` = see the org's triage dashboard
  // and receive the "new report" notification; `update` = change status,
  // resolution notes, duplicate links, and file reports on another user's
  // behalf. Gently staff bypass both (see GENTLY_STAFF_BYPASS_SUBJECTS).
  Feedback = "Feedback",
  ApiKey = "ApiKey",
  // Workflow engine. Not granted by any default group — assign it on a
  // team. Gently staff still pass these checks via the staff bypass so
  // existing operators keep access while customer teams are granted the
  // same actions explicitly. CRUD only; run control and the operator
  // console are their own subjects.
  Workflow = "Workflow",
  // Starting and changing workflow runs: create triggers, reruns, and test
  // runs; update retries, restarts, cancels, and stops.
  WorkflowRun = "WorkflowRun",
  // Operator console that used to key off User.isAdmin: step tester, traffic
  // generator, incident triage, manual socket overrides, hidden steps, and
  // dismissing any approval. Read sees those surfaces; create/update/delete
  // perform them.
  WorkflowAdmin = "WorkflowAdmin",
  WorkflowApiToken = "WorkflowApiToken",
  GuardrailPolicy = "GuardrailPolicy",
  ApprovalConfig = "ApprovalConfig",
  OutOfOffice = "OutOfOffice",
  ResourcePrivilege = "ResourcePrivilege",
  // Applying/removing tags on a resource is itself an access-control
  // decision (a policy tag can grant or revoke visibility), so it is gated
  // separately from ResourcePrivilege (which governs privilege/scope/tag
  // *definition* administration). See `docs/resource-scope-policy/abac_architecture.md`.
  ResourceTag = "ResourceTag",

  // Communications
  SharedMailboxSend = "SharedMailboxSend",
  // Inbound Emails page + list (`read`), re-process / retry-extraction
  // (`update`), and bulk email-rule re-trigger: preview, queue and cancel
  // (`execute`, granted by no default group). Gently staff always pass via
  // `isAdminOrCan`; the rest of the debug tooling (export/import, email rules)
  // stays staff-only.
  InboundEmail = "InboundEmail",

  // Authentication
  // `create` lets a user without a password (SSO-only) register a passkey.
  // Password users can always register one; see PasskeyService.canRegister.
  Passkey = "Passkey",
}

/**
 * Type representing a specific permission
 */
export interface Permission {
  action: Action;
  subject: Subject;
}
